import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Shield } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";
import LogoUploader from "@/components/settings/LogoUploader";
import { canAccessOwnerFrontDeskSettings } from "@/lib/settingsAccess";
import {
  PAYMENT_SCHEDULE_TYPES,
  PAYMENT_SCHEDULE_LABELS,
  PAYMENT_SCHEDULE_DESCRIPTIONS,
  PAYMENT_SCHEDULE_DEFAULT,
} from "@/lib/paymentSchedule";

// =====================================================================
// Settings → Clinic
//   Letterhead (name / address / phone / email / clinic license / logo)
//   Custom plan footer text
//   These are the fields PDF generators + the "send to patient" email
//   already plumb for. Round 1 closes the loop by giving clinic_admins
//   a UI to actually fill them in.
// =====================================================================

// Server may return the enabled list as missing (older row, mid-deploy)
// — fall back to all-enabled so the UI doesn't render an empty toggle
// list and confuse the admin into thinking the clinic offers no plans.
function normalizeEnabled(arr) {
  const set = Array.isArray(arr) ? arr.filter((t) => PAYMENT_SCHEDULE_TYPES.includes(t)) : null;
  if (!set || set.length === 0) return [...PAYMENT_SCHEDULE_TYPES];
  // Preserve canonical order so the toggle list is always consistent.
  return PAYMENT_SCHEDULE_TYPES.filter((t) => set.includes(t));
}

function buildSnapshot(data) {
  return {
    name:                       data?.name                       || "",
    address:                    data?.address                    || "",
    phone:                      data?.phone                      || "",
    email:                      data?.email                      || "",
    prc_license:                data?.prc_license                || "",
    logo_url:                   data?.logo_url                   || "",
    custom_plan_text:           data?.custom_plan_text           || "",
    payment_schedule_type:      data?.payment_schedule_type      || PAYMENT_SCHEDULE_DEFAULT,
    payment_schedules_enabled:  normalizeEnabled(data?.payment_schedules_enabled),
    // Percentage stored as numeric in DB; UI shows the same number
    // (no centavos↔pesos juggling). Empty = null = "any down."
    flexible_min_down_pct:      data?.flexible_min_down_pct != null
                                  ? Number(data.flexible_min_down_pct)
                                  : "",
  };
}

export default function SettingsClinic() {
  const { activeClinic, refreshClinics, clinicRole, isPlatformAdmin } = useAuth();
  const ongoingTreatmentRollout = useOngoingTreatmentRollout();
  const hasOpenCaseCount = Number.isInteger(ongoingTreatmentRollout.openCaseCount)
    && ongoingTreatmentRollout.openCaseCount >= 0;
  const canManageClinicSettings = canAccessOwnerFrontDeskSettings(clinicRole, isPlatformAdmin);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [original, setOriginal] = useState(null);    // last loaded snapshot for diffing
  const [form, setForm]         = useState({
    name: "", address: "", phone: "", email: "", prc_license: "",
    logo_url: "", custom_plan_text: "",
    payment_schedule_type: PAYMENT_SCHEDULE_DEFAULT,
    payment_schedules_enabled: [...PAYMENT_SCHEDULE_TYPES],
    flexible_min_down_pct: "",
  });

  const load = useCallback(async () => {
    if (!canManageClinicSettings) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc("get_clinic_settings");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load clinic settings."); return; }
    const next = buildSnapshot(data);
    setForm(next);
    setOriginal(next);
  }, [canManageClinicSettings]);

  useEffect(() => { load(); }, [load]);

  // Compute a partial-update payload — only fields the user actually
  // changed get sent to the RPC, so we never accidentally clobber
  // a value we didn't load (defense in depth against future schema
  // additions). Array fields need element-wise comparison; reference
  // equality always reports them as different.
  const sameValue = (a, b) => {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((x, i) => x === b[i]);
    }
    return a === b;
  };
  const dirtyPayload = (() => {
    if (!original) return null;
    const out = {};
    for (const k of Object.keys(form)) {
      if (sameValue(form[k], original[k])) continue;
      // Percentage stays as-is. Empty string = null (clears the field,
      // falls back to "any down"). Numeric input clamped to 0–100 by
      // the input element + the RPC's CHECK.
      if (k === "flexible_min_down_pct") {
        const raw = String(form[k] ?? "").trim();
        out.flexible_min_down_pct = raw === "" ? null : Number(raw);
      } else {
        out[k] = form[k];
      }
    }
    return out;
  })();
  const isDirty = !!(dirtyPayload && Object.keys(dirtyPayload).length > 0);

  // Toggle a schedule on/off. If turning OFF the current default, the
  // default auto-promotes to the next enabled type (matches the
  // server's auto-fall-back so the UI doesn't disagree with the DB
  // mid-save). The last enabled toggle is locked to prevent emptying
  // the menu.
  const toggleSchedule = (t) => {
    setForm((f) => {
      const isCurrentlyEnabled = f.payment_schedules_enabled.includes(t);
      let next = isCurrentlyEnabled
        ? f.payment_schedules_enabled.filter((x) => x !== t)
        : [...f.payment_schedules_enabled, t];
      next = PAYMENT_SCHEDULE_TYPES.filter((x) => next.includes(x));
      if (next.length === 0) return f;
      let nextDefault = f.payment_schedule_type;
      if (!next.includes(nextDefault)) {
        nextDefault = next[0];
      }
      return { ...f, payment_schedules_enabled: next, payment_schedule_type: nextDefault };
    });
  };

  async function handleSave() {
    if (!isDirty || saving) return;
    if (!form.name.trim()) {
      toast.error("Clinic name is required.");
      return;
    }
    setSaving(true);
    const { data, error: e } = await supabase.rpc("update_clinic_settings", {
      p_input: dirtyPayload,
    });
    setSaving(false);
    if (e) { toast.error(e.message ?? "Could not save."); return; }
    const next = buildSnapshot(data);
    setForm(next);
    setOriginal(next);
    toast.success("Clinic settings saved.");
    // Clinic name lives on `clinics`; the Auth context's clinics list
    // shows it in the topbar / patient header. Refresh so the sidebar
    // and topbar pick up the new name without a hard reload.
    if (dirtyPayload.name && refreshClinics) await refreshClinics();
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="settings-clinic-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
        <p className="mt-3 text-sm text-slate-500">Loading clinic settings…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700" data-testid="settings-clinic-error">
        {error}
      </div>
    );
  }
  if (!canManageClinicSettings) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="settings-clinic-denied">
        <Shield className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only clinic owners and front desk can manage Letterhead.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-clinic" data-tutorial-id="settings-clinic">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Clinic Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Letterhead and contact info that appear on PDFs and patient-facing emails.
          Only clinic owners and front desk can edit these fields.
        </p>
      </div>

      <div
        className="bg-white rounded-2xl shadow-sm p-6"
        data-testid="ongoing-treatment-rollout-card"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Ongoing treatment tracking (pilot)
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              This pilot is managed by Dental OS. Contact Dental OS if your clinic needs a change.
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
              ongoingTreatmentRollout.loading
                ? "bg-slate-100 text-slate-600"
                : ongoingTreatmentRollout.enabled
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-700"
            }`}
            data-testid="ongoing-treatment-rollout-status"
          >
            {ongoingTreatmentRollout.loading
              ? "Checking"
              : ongoingTreatmentRollout.enabled
                ? "On"
                : "Off"}
          </span>
        </div>
        <p className="mt-4 text-sm text-slate-700" data-testid="ongoing-treatment-open-count">
          {hasOpenCaseCount ? (
            <>
              {ongoingTreatmentRollout.openCaseCount}{" "}
              {ongoingTreatmentRollout.openCaseCount === 1 ? "open case" : "open cases"}
            </>
          ) : "Open-case count unavailable"}
        </p>
        {ongoingTreatmentRollout.error ? (
          <p className="mt-2 text-xs text-amber-700" data-testid="ongoing-treatment-rollout-diagnostic">
            Status could not be confirmed, so ongoing treatment tracking is safely treated as Off.
          </p>
        ) : null}
      </div>

      {/* Logo card */}
      <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="settings-clinic-logo-card" data-tutorial-id="settings-clinic-logo-card">
        <h2 className="text-base font-semibold text-slate-900">Clinic logo</h2>
        <p className="mt-1 text-xs text-slate-500">
          Square works best (e.g. 512×512). PNG, JPEG, WEBP, or SVG · max 2 MB.
        </p>

        <div className="mt-4">
          <LogoUploader
            clinicId={activeClinic?.clinic_id || activeClinic?.id}
            clinicName={form.name || activeClinic?.clinic_name}
            logoUrl={form.logo_url}
            testIdPrefix="settings-clinic-logo"
            onChange={(url) => {
              setForm((f) => ({ ...f, logo_url: url }));
              setOriginal((o) => o ? { ...o, logo_url: url } : o);
            }}
          />
        </div>
      </div>

      {/* Letterhead form card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4" data-testid="settings-clinic-letterhead-card" data-tutorial-id="settings-clinic-letterhead-card">
        <h2 className="text-base font-semibold text-slate-900">Letterhead</h2>

        <Field
          id="clinic-name" label="Clinic name" required
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v }))}
          placeholder="e.g. Bright Smile Dental Clinic"
          maxLength={120}
        />

        <Field
          id="clinic-address" label="Address" textarea rows={2}
          value={form.address}
          onChange={(v) => setForm((f) => ({ ...f, address: v }))}
          placeholder="Street, city, province, ZIP"
          maxLength={500}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="clinic-phone" label="Phone"
            value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
            placeholder="+63 2 8123 4567"
            maxLength={40}
          />
          <Field
            id="clinic-email" label="Contact email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            placeholder="hello@yourclinic.com"
            maxLength={120}
            type="email"
            hint="Display only — system emails go from your clinic's verified sender."
          />
        </div>

        <Field
          id="clinic-prc" label="PRC license"
          value={form.prc_license}
          onChange={(v) => setForm((f) => ({ ...f, prc_license: v }))}
          placeholder="e.g. PRC #123456"
          maxLength={60}
          hint="Clinic-level license for clinic documents. Tx Plans use the attending doctor's PRC from Settings > Team."
        />
      </div>

      {/* Payment plans card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4" data-testid="settings-clinic-payment-schedule-card">
        <h2 className="text-base font-semibold text-slate-900">Payment plans</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Toggle which payment plans this clinic offers. The default is what the
          treatment plan PDF uses on patient proposals. Final payment terms are
          confirmed at checkout of the first visit.
        </p>

        <div className="space-y-2">
          {PAYMENT_SCHEDULE_TYPES.map((t) => {
            const isEnabled = form.payment_schedules_enabled.includes(t);
            const isDefault = form.payment_schedule_type === t;
            const wouldEmptyMenu = isEnabled && form.payment_schedules_enabled.length === 1;
            return (
              <label
                key={t}
                data-testid={`payment-schedule-row-${t}`}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${isEnabled ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50"} ${wouldEmptyMenu ? "cursor-not-allowed opacity-90" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={isEnabled}
                  disabled={wouldEmptyMenu}
                  onChange={() => toggleSchedule(t)}
                  data-testid={`payment-schedule-toggle-${t}`}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${isEnabled ? "text-slate-900" : "text-slate-400"}`}>
                      {PAYMENT_SCHEDULE_LABELS[t]}
                    </span>
                    {isDefault && isEnabled ? (
                      <span
                        data-testid={`payment-schedule-default-badge-${t}`}
                        className="text-[10px] uppercase font-bold tracking-wider text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded"
                      >
                        Default
                      </span>
                    ) : null}
                    {wouldEmptyMenu ? (
                      <span className="text-[10px] text-slate-400 italic">
                        At least one plan must stay enabled
                      </span>
                    ) : null}
                  </div>
                  <p className={`text-xs mt-0.5 ${isEnabled ? "text-slate-500" : "text-slate-400"}`}>
                    {PAYMENT_SCHEDULE_DESCRIPTIONS[t]}
                  </p>
                  {t === "custom" && isEnabled ? (
                    <div className="mt-2 flex items-center gap-2">
                      <label
                        htmlFor="flexible-min-down"
                        className="text-xs text-slate-700 whitespace-nowrap"
                      >
                        Minimum down payment (%)
                      </label>
                      <input
                        id="flexible-min-down"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={form.flexible_min_down_pct}
                        onChange={(e) => setForm((f) => ({ ...f, flexible_min_down_pct: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="e.g. 20"
                        data-testid="field-flexible-min-down"
                        className="w-28 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                      />
                      <span className="text-[11px] text-slate-400">
                        Leave blank for any down payment
                      </span>
                    </div>
                  ) : null}
                </div>
              </label>
            );
          })}
        </div>

        <div>
          <label htmlFor="payment-schedule-type" className="block text-sm font-medium text-slate-700 mb-1">
            Default plan (used on the proposal PDF)
          </label>
          <select
            id="payment-schedule-type"
            value={form.payment_schedule_type}
            onChange={(e) => setForm((f) => ({ ...f, payment_schedule_type: e.target.value }))}
            data-testid="field-payment-schedule-type"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
          >
            {form.payment_schedules_enabled.map((t) => (
              <option key={t} value={t}>{PAYMENT_SCHEDULE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Custom plan footer */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4" data-testid="settings-clinic-plan-text-card">
        <h2 className="text-base font-semibold text-slate-900">Treatment plan footer</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Optional text shown at the bottom of every Tx Plan PDF (e.g. payment terms, disclaimers).
        </p>
        <Field
          id="custom-plan-text" label="" textarea rows={4}
          value={form.custom_plan_text}
          onChange={(v) => setForm((f) => ({ ...f, custom_plan_text: v }))}
          placeholder="e.g. Payment terms: 50% on commencement, balance on completion."
          maxLength={2000}
        />
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 flex items-center justify-end gap-2 bg-white rounded-2xl shadow-md p-3 border border-slate-100" data-tutorial-id="settings-clinic-save-bar">
        <span className="text-xs text-slate-500 mr-auto pl-2">
          {isDirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <button
          type="button"
          onClick={() => { if (original) setForm(original); }}
          disabled={!isDirty || saving}
          data-testid="settings-clinic-cancel"
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          data-testid="settings-clinic-save"
          className="px-5 py-2 text-sm font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ---- Tiny field helper -----------------------------------------------
function Field({ id, label, value, onChange, placeholder, maxLength, type = "text", textarea, rows = 2, required, hint }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <div>
      {label ? (
        <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
          {label}{required ? <span className="text-rose-500"> *</span> : null}
        </label>
      ) : null}
      <Comp
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        type={textarea ? undefined : type}
        rows={textarea ? rows : undefined}
        data-testid={`field-${id}`}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 resize-y"
      />
      {hint ? <p className="mt-1 text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}
